import { Logger } from '../../Logger';
import { logConstants } from '../../../utils/Constants';
import { ParsedRequirementNode } from '../../../Common/Parsers/RequirementNodeParser';

interface Credential {
	id: string;
	value: string;
}

export enum CredentialType {
	None = 'none',
	Username = 'username',
	Domain = 'domain',
	Password = 'password',
	NewPassword = 'newpassword',
	Passcode = 'passcode',
	Pin = 'pin',
	TextCredential = 'textcredential',
	SaveCredentials = 'savecredentials',
	Realm = 'realm',
	RsaPasscode = 'rsa-passcode',
	RsaNextPasscode = 'rsa-next-passcode',
	RsaNextTokenCode = 'rsa-next-tokencode',
	RsaPin = 'rsa-pin',
	WebView = 'webview',
}

export default class AuthForm {
	private requirements: ParsedRequirementNode[];
	private saveCredentialsID: string;
	private userNameID = '';
	private domainID = '';

	constructor(requirements: ParsedRequirementNode[], allowSavedCredentials = true) {
		this.requirements = requirements;
		this.updateSaveCredentialsCheckbox(allowSavedCredentials);
		this.validateAndSetIDs();
	}

	getRequirements(): ParsedRequirementNode[] {
		return this.requirements;
	}

	getSaveCredentialsID(): string {
		return this.saveCredentialsID;
	}

	getUserNameID(): string {
		return this.userNameID;
	}

	getDomainID(): string {
		return this.domainID;
	}

	isUserNameOrDomainRequirement(credentialID: string): boolean {
		return credentialID === this.userNameID || credentialID === this.domainID;
	}

	hasSaveID(credentialID: string): boolean {
		for (const requirement of this.requirements) {
			if (requirement.credential.id === credentialID) {
				return !!requirement.credential.saveID;
			}
		}
	}

	getSingleButtonCredential(): Credential {
		let buttonCount = 0;
		let credential: Credential;
		for (const requirement of this.requirements) {
			if (requirement.input.button) {
				credential = { id: requirement.credential.id, value: requirement.input.button };
				++buttonCount;
			}
		}

		if (buttonCount != 1) {
			throw new Error(`AuthForm: Form contains ${buttonCount} buttons.`);
		}

		return credential;
	}

	static isSaveable(requirement: ParsedRequirementNode): boolean {
		return (
			this.canSaveCredentialType(requirement) &&
			this.canSaveInputType(requirement) &&
			!!requirement.credential.saveID &&
			// Read only credentials don't have values entered by the user, so not considered saveable.
			!requirement.input.text.readOnly
		);
	}

	private displaySaveCredentialsCheckbox(): boolean {
		let hasSaveCredentialsRequirement = false,
			hasSaveableRequirement = false;
		for (const requirement of this.requirements) {
			hasSaveCredentialsRequirement ||= this.isSaveCredentialsRequirement(requirement);
			hasSaveableRequirement ||= AuthForm.isSaveable(requirement);
			if (hasSaveCredentialsRequirement && hasSaveableRequirement) {
				return true;
			}
		}

		Logger.getInstance().write(
			logConstants.INFO,
			'AuthForm: Hide SaveCredentials checkbox.'
		);
		return false;
	}

	private updateSaveCredentialsCheckbox(
		allowSavedCredentials: boolean
	): ParsedRequirementNode[] {
		if (!allowSavedCredentials || !this.displaySaveCredentialsCheckbox()) {
			Logger.getInstance().write(
				logConstants.INFO,
				'AuthForm: Removing save credentials checkbox requirement.'
			);
			this.requirements = this.requirements.filter(
				(requirement) =>
					requirement.credential.type.toLowerCase() !== CredentialType.SaveCredentials
			);
		}
		return this.requirements;
	}

	private isSaveCredentialsRequirement(requirement: ParsedRequirementNode): boolean {
		return requirement.credential.type === CredentialType.SaveCredentials;
	}

	private static canSaveCredentialType(requirement: ParsedRequirementNode): boolean {
		switch (requirement.credential.type.toLowerCase()) {
			case CredentialType.Username:
			case CredentialType.Domain:
			case CredentialType.Realm:
			case CredentialType.Password:
			case CredentialType.NewPassword:
			case CredentialType.TextCredential:
				return true;

			default:
				return false;
		}
	}

	private static canSaveInputType(requirement: ParsedRequirementNode): boolean {
		return (
			!!requirement.input.text ||
			!!requirement.input.radioButton ||
			!!requirement.input.comboBox ||
			!!requirement.input.multiComboBox ||
			!!requirement.input.checkBox
		);
	}

	private validateAndSetIDs(): void {
		for (const requirement of this.requirements) {
			if (this.isSaveCredentialsRequirement(requirement)) {
				if (!requirement.credential.id) {
					throw new Error(
						'AuthForm: saveCredentials requirement present but no credential id supplied'
					);
				}

				if (!!this.saveCredentialsID) {
					throw new Error('AuthForm: Too many saveCredentials requirements');
				}

				this.saveCredentialsID = requirement.credential.id;
			} else if (requirement.credential.type === CredentialType.Username) {
				if (!requirement.credential.id) {
					throw new Error(
						'AuthForm: username requirement present but no credential id supplied'
					);
				}

				if (!!this.userNameID) {
					throw new Error('AuthForm: Too many username requirements');
				}

				this.userNameID = requirement.credential.id;
			} else if (requirement.credential.type === CredentialType.Domain) {
				if (!requirement.credential.id) {
					throw new Error(
						'AuthForm: domain requirement present but no credential id supplied'
					);
				}

				if (!!this.domainID) {
					throw new Error('AuthForm: Too many domain requirements');
				}

				this.domainID = requirement.credential.id;
			}
		}
	}
}
